// Welcome-offer bar (SS-13) under the app header: "20% off your first credit
// purchase — ends in 3 days". The server sends `offer` on GET /me/onboarding
// only while the org's offer is live; the bar also hides itself at endsAt,
// for 24 h after ×, while impersonating, and for platform staff.
import React, {useEffect, useState} from 'react';
import {Box, Button, IconButton, Typography} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {useHistory} from 'react-router-dom';

import {isAdminPanelRole} from '../../auth/roles';
import {isImpersonating} from '../../lib/impersonation';
import useMyOnboarding from '../../lib/useMyOnboarding';
import {getFromLocalStorage} from '../../lib/utils';
import {offerCountdown, offerMsLeft} from '../../lib/welcomeOffer';
import {routeCodes} from '../../views/App';

export const OFFER_BANNER_HEIGHT = 40;
export const OFFER_BANNER_HIDDEN_KEY = 'combird_offer_banner_hidden_until';
const HIDE_FOR_MS = 24 * 60 * 60 * 1000;
const TICK_MS = 60 * 1000;

const readHiddenUntil = () => {
  try {
    return Number(window.localStorage.getItem(OFFER_BANNER_HIDDEN_KEY)) || 0;
  } catch {
    return 0;
  }
};

// The bar and its layout spacer are separate components; × must hide both.
const hideListeners = new Set();

const hideForADay = () => {
  const until = Date.now() + HIDE_FOR_MS;
  try {
    window.localStorage.setItem(OFFER_BANNER_HIDDEN_KEY, String(until));
  } catch {
    // no storage: hidden until the next remount
  }
  hideListeners.forEach((notify) => notify(until));
};

export const useOfferBanner = () => {
  const onboarding = useMyOnboarding();
  const [now, setNow] = useState(() => Date.now());
  const [hiddenUntil, setHiddenUntil] = useState(readHiddenUntil);
  const offer = (onboarding && onboarding.offer) || null;
  const endsAt = offer ? offer.endsAt : '';

  useEffect(() => {
    hideListeners.add(setHiddenUntil);
    return () => {
      hideListeners.delete(setHiddenUntil);
    };
  }, []);

  // Re-render every minute for the countdown, and exactly at endsAt to hide.
  useEffect(() => {
    const left = endsAt ? offerMsLeft(endsAt) : 0;
    if (!(left > 0)) {
      return undefined;
    }
    const timer = setTimeout(() => setNow(Date.now()), Math.min(TICK_MS, left));
    return () => clearTimeout(timer);
  }, [endsAt, now]);

  const countdown = offer ? offerCountdown(endsAt, now) : '';
  const isPlatformStaff = isAdminPanelRole((getFromLocalStorage() || {}).role);
  const visible = !!countdown && now >= hiddenUntil && !isImpersonating() && !isPlatformStaff;
  return {offer, countdown, visible};
};

const OfferBanner = () => {
  const history = useHistory();
  const {offer, countdown, visible} = useOfferBanner();
  if (!visible) {
    return null;
  }
  return (
    <Box
      role="status"
      sx={{
        height: OFFER_BANNER_HEIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center',
        gap: 1.5, px: 2, background: '#fff3e0', color: '#5d4037', fontSize: 14
      }}
    >
      <Typography variant="body2" noWrap sx={{minWidth: 0}}>
        🎉 {offer.percentOff}% off your first credit purchase — ends in {countdown}
      </Typography>
      {offer.code ?
        <Typography variant="body2" noWrap sx={{display: {xs: 'none', sm: 'block'}}}>
          Code: <b>{offer.code}</b>
        </Typography>
        : null}
      <Button
        size="small"
        variant="contained"
        color="warning"
        sx={{textTransform: 'none', py: 0, flexShrink: 0}}
        onClick={ () => history.push(routeCodes.CREDIT_HISTORY) }
      >
        Buy credits
      </Button>
      <IconButton size="small" aria-label="Hide offer" onClick={ hideForADay } sx={{color: 'inherit'}}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};

// Keeps page content (and the drawer) below the fixed app bar when the bar shows.
export const OfferBannerSpacer = () => (useOfferBanner().visible ? <Box sx={{height: OFFER_BANNER_HEIGHT}} /> : null);

export default OfferBanner;
